import { cn } from "@/lib/utils"
import { useRef, useState, type MouseEvent } from "react"

interface SpotlightCardProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: React.ElementType
  spotlightColor?: string
}

export function SpotlightCard({
  as: Component = "div",
  className,
  children,
  spotlightColor = "rgba(59, 130, 246, 0.12)", // brand-blue default
  ...props
}: SpotlightCardProps) {
  const divRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [opacity, setOpacity] = useState(0)

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return

    const div = divRef.current
    const rect = div.getBoundingClientRect()

    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top })
  }

  const handleFocus = () => {
    setOpacity(1)
  }

  const handleBlur = () => {
    setOpacity(0)
  }

  const handleMouseEnter = () => {
    setOpacity(1)
  }

  const handleMouseLeave = () => {
    setOpacity(0)
  }

  return (
    <Component
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "group relative overflow-hidden rounded-2xl sm:rounded-3xl",
        // Liquid Glass Surface tier - Light mode
        "bg-gradient-to-br from-white/90 via-white/80 to-white/90",
        // Liquid Glass Surface tier - Dark mode (using CSS variables)
        "dark:from-[hsl(var(--card)/0.90)] dark:via-[hsl(var(--card)/0.80)] dark:to-[hsl(var(--card)/0.90)]",
        "backdrop-blur-lg backdrop-saturate-[180%]",
        "border border-white/30 dark:border-border/30",
        // Layered shadow with inset specular
        "shadow-[0_4px_24px_-8px_hsl(220_20%_20%/0.1),inset_0_1px_0_hsl(0_0%_100%/0.6)]",
        "dark:shadow-[0_4px_24px_-8px_hsl(220_20%_0%/0.3),inset_0_1px_0_hsl(0_0%_100%/0.1)]",
        // Hover — elevate with glow
        "hover:bg-white/95 dark:hover:bg-card/95",
        "hover:border-blue-200/50 dark:hover:border-blue-500/30",
        "hover:shadow-[0_8px_32px_-8px_hsl(220_50%_40%/0.15),inset_0_1px_0_hsl(0_0%_100%/0.8)]",
        "dark:hover:shadow-[0_8px_32px_-8px_hsl(220_50%_40%/0.25),inset_0_1px_0_hsl(0_0%_100%/0.15)]",
        // 240ms canonical transition
        "transition-all duration-240 ease-out",
        className
      )}
      {...props}
    >
      {/* Specular highlight — Liquid Glass signature */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/20 z-10 rounded-t-2xl sm:rounded-t-3xl" />

      {/* Interactive spotlight effect */}
      <div
        className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-240 z-0"
        style={{
          opacity,
          background: `radial-gradient(500px circle at ${position.x}px ${position.y}px, ${spotlightColor}, transparent 50%)`,
        }}
      />

      <div className="relative h-full z-10 flex flex-col">{children}</div>
    </Component>
  )
}

